import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { Reveal, CountUp, AnimatedBar } from "../components/Animate";
import {
  Briefcase, Users, ShieldCheck, Filter, Zap, BarChart3, BadgeCheck,
  Workflow, Target, Sparkles, ArrowUpRight, ArrowRight, Building2,
  TrendingUp, Clock, ChevronDown, X, AlertTriangle, FileSearch,
  Megaphone, CheckCircle2, Brain, Globe, Mail,
} from "lucide-react";

const C = {
  green: "#006CFF", greenDeep: "#0F7C39", navy: "#0040A8", navyDeep: "#0A1F44",
  orange: "#FF8A00", purple: "#6C5CE7", red: "#DC2626",
  ink: "#0F172A", paper: "#F5F7FA", rule: "#E5E7EB",
};

function Pill({ children, color = C.green }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"
      style={{ background: `${color}15`, color }}>{children}</span>
  );
}

function FeatureCard({ icon: Icon, title, body, color, delay = 0 }) {
  return (
    <Reveal delay={delay} className="group rounded-2xl border bg-white p-6 float-hover hover:shadow-xl"
      style={{ borderColor: C.rule }} data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-12 h-12 rounded-xl grid place-items-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
        style={{ background: `${color}15`, color }}><Icon size={24} /></div>
      <h3 className="font-display font-bold text-lg mt-4" style={{ color: C.ink }}>{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </Reveal>
  );
}

function FAQ({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white" style={{ borderColor: C.rule }}>
      <button onClick={() => setOpen(v => !v)} className="w-full text-left p-5 flex items-center justify-between gap-4">
        <span className="font-semibold text-sm md:text-base" style={{ color: C.ink }}>{q}</span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: C.green }} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden"><div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</div></div>
      </div>
    </div>
  );
}

function StepNode({ n, icon: Icon, title, color }) {
  return (
    <div className="flex-1 min-w-[120px] flex flex-col items-center text-center">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl grid place-items-center text-white shadow-md transition-transform hover:scale-110"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
          <Icon size={26} />
        </div>
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border text-[10px] font-bold grid place-items-center"
          style={{ borderColor: C.rule, color: C.ink }}>{n}</span>
      </div>
      <div className="mt-3 font-bold text-sm" style={{ color: C.ink }}>{title}</div>
    </div>
  );
}
function Arrow() { return <div className="hidden md:flex items-center text-slate-300"><ArrowRight size={18} /></div>; }

export default function CompaniesPublic() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div className="min-h-screen bg-white text-ink">
      <PublicNavBar primaryCta={{ to: "/signup?role=company", label: "Post a job" }} />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: C.paper }}>
        <div className="absolute inset-0 -z-10 opacity-60"
          style={{ background: `radial-gradient(60% 50% at 90% 0%, ${C.orange}18 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, ${C.green}12 0%, transparent 60%)` }} />
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Pill color={C.orange}>For Companies</Pill>
            <h1 className="font-display font-black mt-5 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase" style={{ color: C.navyDeep }}>
              Hire <span style={{ color: C.green }}>Verified Talent.</span><br />
              Build <span style={{ color: C.green }}>Stronger Teams.</span>
            </h1>
            <p className="text-slate-600 mt-5 text-lg max-w-xl leading-relaxed">
              Post jobs, filter by verified skills and CoE badges, shortlist faster, and hire from a curated pipeline of India&apos;s best engineering talent.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup?role=company" data-testid="hero-cta-post" className="px-6 py-3.5 rounded-lg font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDeep} 100%)` }}>
                Post a job <ArrowUpRight size={16} />
              </Link>
              <a href="#how" className="px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2 border-2 transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: C.navy, color: C.navy }}>
                See how it works <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Right: animated hiring funnel preview */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white shadow-2xl border p-6 relative overflow-hidden" style={{ borderColor: C.rule }}>
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: C.orange }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: C.navy }}>
                  <Filter size={14} /> Talent Funnel
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    { label: "Applicants",    pct: 100, n: 1240, color: C.navy },
                    { label: "Verified Skills", pct: 56, n: 690, color: C.green },
                    { label: "CoE Badged",   pct: 28, n: 348, color: C.purple },
                    { label: "Shortlisted",  pct: 9,  n: 112, color: C.orange },
                    { label: "Hired",        pct: 2,  n: 24,  color: C.greenDeep },
                  ].map((s, i) => (
                    <Reveal key={s.label} delay={i * 100}>
                      <div className="flex justify-between text-xs font-medium"><span>{s.label}</span><span><CountUp value={s.n} duration={1200 + i * 100} /></span></div>
                      <AnimatedBar pct={s.pct} color={s.color} delay={i * 100} />
                    </Reveal>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-lg p-3 border text-center" style={{ borderColor: C.rule }}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Median Time-to-Hire</div>
                    <div className="font-display font-black text-xl mt-1" style={{ color: C.green }}>
                      <CountUp value={9} suffix=" days" duration={1300} />
                    </div>
                  </div>
                  <div className="rounded-lg p-3 border text-center" style={{ borderColor: C.rule }}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Match Quality</div>
                    <div className="font-display font-black text-xl mt-1" style={{ color: C.orange }}>
                      <CountUp value={94} suffix="%" duration={1500} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Promise bar */}
        <div className="border-t bg-white" style={{ borderColor: C.rule }}>
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              [ShieldCheck, "Verified Skills",   "No CV exaggeration",     C.green],
              [Filter,      "Precision Filters", "By CoE, skill, location", C.navy],
              [Zap,         "< 72 hrs",          "Shortlist time",          C.orange],
              [BarChart3,   "Pipeline Analytics", "Hire smarter",           C.purple],
            ].map(([Icon, t, s, color], i) => (
              <div key={t} className="group flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg grid place-items-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: `${color}15`, color }}>
                  <Icon size={20} />
                </div>
                <div className="leading-tight"><div className="font-bold text-sm" style={{ color: C.ink }}>{t}</div><div className="text-xs text-slate-500">{s}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.red}>The hiring problem you keep paying for</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Why is engineering hiring so <span style={{ color: C.red }}>broken?</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6 mt-12 items-start">
            <ul className="lg:col-span-2 space-y-3">
              {[
                "Resumes hide as much as they reveal",
                "Unfit candidates eat up your TA team&apos;s bandwidth",
                "Final-round drop-offs from skill mismatches",
                "First-90-day attrition from rushed hires",
                "High agency fees for entry-level placements",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 p-4 rounded-xl bg-white border" style={{ borderColor: C.rule }}>
                  <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center" style={{ background: `${C.red}10`, color: C.red }}><X size={18} /></span>
                  <span className="text-sm font-medium pt-1.5" style={{ color: C.ink }}>{p}</span>
                </li>
              ))}
            </ul>
            <div className="rounded-2xl p-6 border-2 border-dashed text-center" style={{ borderColor: `${C.red}55`, background: `${C.red}05` }}>
              <AlertTriangle size={28} className="mx-auto" style={{ color: C.red }} />
              <div className="font-display font-black text-lg mt-3" style={{ color: C.red }}>WITHOUT SKILLCONNECT</div>
              <div className="mt-4 text-sm space-y-2 text-slate-700">
                <div>Unverified Claims</div>
                <div>Wasted Interviews</div>
                <div>High Attrition</div>
                <div className="font-semibold" style={{ color: C.red }}>Slow Hiring</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT HELPS */}
      <section id="how" className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.green}>What you&apos;ll do on SkillConnect</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Post · Filter · Interview · <span style={{ color: C.green }}>Hire</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            <FeatureCard icon={Briefcase} color={C.green}  delay={0}   title="Post Jobs in 2 Minutes"
              body="Reusable templates, eligibility filters, and approval flows make every opening fast to publish." />
            <FeatureCard icon={Filter}    color={C.navy}   delay={80}  title="Filter by Verified Skill"
              body="Stop chasing keywords. Filter by SkillConnect-verified skills and tamper-proof CoE badges." />
            <FeatureCard icon={BadgeCheck} color={C.purple} delay={160} title="See CoE Badges & Scores"
              body="Each candidate shows a live employability score plus the CoE labs they've actually passed." />
            <FeatureCard icon={Workflow}   color={C.orange} delay={240} title="Stage-Based Pipeline"
              body="Drag candidates across stages, leave notes, schedule interviews — all in one tab." />
            <FeatureCard icon={FileSearch} color={C.green}  delay={320} title="External Talent Pool"
              body="Tap into our auto-aggregated job/talent feeds across LinkedIn, Naukri, and more (via partners)." />
            <FeatureCard icon={BarChart3}  color={C.navy}   delay={400} title="Hiring Analytics"
              body="Track funnel drop-off, source ROI, and offer-acceptance rates with live dashboards." />
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.orange}>Your hiring journey</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              From <span style={{ color: C.green }}>job spec</span> to signed offer in days, not months.
            </h2>
          </div>
          <div className="mt-12 flex flex-wrap items-start justify-center gap-y-8 gap-x-2 md:gap-x-4">
            <StepNode n="1" icon={Briefcase}   title="Post"        color={C.green} /><Arrow />
            <StepNode n="2" icon={Filter}      title="Filter"      color={C.navy} /><Arrow />
            <StepNode n="3" icon={Megaphone}   title="Shortlist"   color={C.orange} /><Arrow />
            <StepNode n="4" icon={Brain}       title="Interview"   color={C.purple} /><Arrow />
            <StepNode n="5" icon={CheckCircle2} title="Hire"       color={C.green} />
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.purple}>Outcomes that matter</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Real impact, measured every quarter.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Users,       v: 10000, suffix: "+",     l: "Verified Candidates", c: C.green },
              { icon: Building2,   v: 200,   suffix: "+",     l: "Companies Hiring",    c: C.navy   },
              { icon: Clock,       v: 9,     suffix: " days", l: "Median Time-to-Hire", c: C.orange },
              { icon: TrendingUp,  v: 30,    suffix: "%",     l: "Cost-per-Hire Savings", c: C.purple },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 100} className="rounded-2xl border bg-white p-5 float-hover" style={{ borderColor: C.rule }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${s.c}15`, color: s.c }}><s.icon size={22} /></div>
                <div className="mt-4 font-display font-black text-2xl md:text-3xl tracking-tight" style={{ color: s.c }}>
                  <CountUp value={s.v} suffix={s.suffix} />
                </div>
                <div className="mt-1 text-sm font-medium text-slate-600">{s.l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.green}>Recruiter stories</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Hiring teams love the <span style={{ color: C.green }}>signal-to-noise</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {[
              { name: "Priya Iyer",    role: "Head of Talent · Infotech Solutions", quote: "We closed our React intern hiring round in 8 days. Verified skills meant zero wasted interviews.", color: C.green },
              { name: "Vikram Joshi",  role: "Engineering Lead · BlueCloud Labs",  quote: "CoE badges are our new must-have filter. Quality of shortlists jumped overnight.", color: C.navy },
              { name: "Riya Khanna",   role: "HR · NovaFin Analytics",             quote: "Cut our cost-per-hire by ~30%. The funnel analytics finally tell us what's working.", color: C.orange },
            ].map((t) => (
              <div key={t.name} className="rounded-2xl border bg-white p-6 float-hover" style={{ borderColor: C.rule }}>
                <div className="text-4xl leading-none" style={{ color: t.color }}>&ldquo;</div>
                <p className="text-sm text-slate-700 leading-relaxed mt-2">{t.quote}</p>
                <div className="mt-5 pt-4 border-t flex items-center gap-3" style={{ borderColor: C.rule }}>
                  <div className="w-10 h-10 rounded-full grid place-items-center text-white font-bold text-sm" style={{ background: `linear-gradient(135deg, ${t.color} 0%, ${C.navy} 100%)` }}>
                    {t.name.split(" ").map(p => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="leading-tight">
                    <div className="font-bold text-sm" style={{ color: C.ink }}>{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center">
            <Pill color={C.navy}>FAQs</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>For recruiters & hiring managers.</h2>
          </div>
          <div className="mt-10 space-y-3">
            <FAQ defaultOpen q="How is skill verification done?"
              a="Every CoE Lab module ends with a proctored MCQ + a project assessment. Pass with 70%+ to earn a tamper-proof certificate. As a recruiter you see only the badged, score-verified candidates." />
            <FAQ q="What's the pricing for companies?"
              a="Posting jobs is free. We charge a flat monthly subscription based on team size + a small per-hire success fee — drastically cheaper than agencies. Talk to sales for a custom quote." />
            <FAQ q="Can we integrate with our ATS?"
              a="Yes — we expose REST APIs and webhooks for popular ATS systems (Greenhouse, Lever, Workday) so candidates flow straight into your existing pipeline." />
            <FAQ q="What kind of roles work best here?"
              a="Engineering & technical roles: software, embedded, EV, semiconductor, AI/ML, cybersecurity, IoT, biomedical, aerospace/defence, electronics manufacturing." />
            <FAQ q="How fast can we start?"
              a="Sign up in 2 minutes, post your first job in 5. Most companies see their first verified shortlist within 48 hours." />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="text-white relative overflow-hidden" style={{ background: C.navyDeep }}>
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-25 blur-3xl" style={{ background: C.green }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: C.orange }} />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">
          <Pill color={C.green}>Ready to hire smarter?</Pill>
          <h2 className="font-display font-black text-3xl md:text-5xl mt-4 leading-tight">
            POST A JOB. SEE <span style={{ color: C.green }}>VERIFIED CANDIDATES</span>.<br />
            HIRE IN DAYS.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup?role=company" className="px-7 py-4 rounded-lg font-bold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:scale-[1.02]" style={{ background: C.green }} data-testid="final-cta-post">
              Post your first job <ArrowUpRight size={18} />
            </Link>
            <a href="mailto:info@skillconnect.com" className="px-7 py-4 rounded-lg font-bold inline-flex items-center gap-2 border-2 border-white/30 text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-1">
              Talk to sales
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-white/80">
            <div className="flex flex-wrap gap-6">
              <span className="inline-flex items-center gap-2"><Globe size={14} style={{ color: C.green }} /> skillconnect.com</span>
              <span className="inline-flex items-center gap-2"><Mail size={14} style={{ color: C.green }} /> info@skillconnect.com</span>
            </div>
            <div className="font-display font-extrabold text-xs tracking-widest uppercase" style={{ color: C.green }}>
              Verified Skills · Better Matches · Faster Hires
            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
